import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, CheckCircle2, XCircle, Award, Monitor, Clock, Layers, Megaphone } from "lucide-react";
import { Link } from "wouter";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useAuth } from "@/_core/hooks/useAuth";
import { useState, useCallback, useMemo } from "react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getRandomQuestions } from "@/data/sampleQuestions";

function SampleQuiz() {
  const [questions, setQuestions] = useState(() => getRandomQuestions(5));
  const [current, setCurrent] = useState(0);
  const [score, setScore] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [finished, setFinished] = useState(false);

  const q = questions[current];

  const handleSelect = useCallback((index: number) => {
    if (selected !== null) return;
    setSelected(index);
    if (index === q.correct) setScore((s) => s + 1);
  }, [selected, q.correct]);

  const handleNext = useCallback(() => {
    if (current < questions.length - 1) {
      setCurrent((c) => c + 1);
      setSelected(null);
    } else {
      setFinished(true);
    }
  }, [current]);

  const restart = useCallback(() => {
    setQuestions(getRandomQuestions(5));
    setCurrent(0);
    setScore(0);
    setSelected(null);
    setFinished(false);
  }, []);

  if (finished) {
    const pct = Math.round((score / questions.length) * 100);
    const shareText = `Biz-AI検定のサンプル問題に挑戦！結果は ${score}/${questions.length} 正解（${pct}%）でした。あなたも挑戦してみませんか？`;
    const shareUrl = "https://examcenter-bizai.com/#sample";
    const xShareUrl = `https://x.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    const threadsShareUrl = `https://www.threads.net/intent/post?text=${encodeURIComponent(shareText + "\n" + shareUrl)}`;
    const lineShareUrl = `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
    const igShareText = shareText + "\n" + shareUrl;

    return (
      <div className="text-center py-10">
        <div className="text-6xl mb-6">{pct >= 80 ? "🎉" : pct >= 60 ? "👍" : "📚"}</div>
        <h3 className="text-2xl font-bold text-slate-800 mb-3">
          {score} / {questions.length} 正解（{pct}%）
        </h3>
        <p className="text-sm text-slate-600 mb-8 max-w-md mx-auto leading-relaxed">
          {pct >= 80
            ? "素晴らしい結果です！本試験でも実力を発揮できるでしょう。"
            : pct >= 60
            ? "良い感覚をお持ちです。本試験に向けて実務経験を振り返ってみましょう。"
            : "AI活用の基礎を学ぶ良い機会です。本試験を目標に知識を深めてみませんか？"}
        </p>

        {/* SNSシェアボタン */}
        <div className="mb-8">
          <p className="text-xs text-slate-400 mb-3">結果をシェアして友達にも挑戦してもらおう</p>
          <div className="flex justify-center gap-2 flex-wrap">
            <a href={xShareUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-black text-white text-xs font-medium hover:bg-gray-800 transition-colors">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              X
            </a>
            <a href={threadsShareUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-black text-white text-xs font-medium hover:bg-gray-800 transition-colors">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.59 12c.025 3.086.718 5.496 2.057 7.164 1.432 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.187.408-2.26 1.33-3.017.88-.724 2.082-1.13 3.478-1.171 1.005-.03 1.927.088 2.755.353.007-.678-.058-1.297-.196-1.836l2.07-.36c.2.803.296 1.7.286 2.668l.013.148c1.166.55 2.063 1.378 2.598 2.4.738 1.412.895 3.63-.595 5.822l.002-.003c-1.738 2.543-4.487 3.235-7.771 3.255zm-1.693-8.264c-.988.026-1.756.274-2.286.737-.476.417-.692.937-.664 1.592.057 1.338 1.304 2.08 2.784 2.001 1.068-.058 1.9-.46 2.473-1.173.466-.581.79-1.378.946-2.387-.776-.255-1.643-.397-2.59-.396-.224 0-.445.01-.663.026z"/></svg>
              Threads
            </a>
            <button onClick={() => { navigator.clipboard?.writeText(igShareText).then(() => toast.success("テキストをコピーしました！Instagramのストーリーズに貼り付けてシェアできます。")).catch(() => toast.error("コピーに失敗しました")); }}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r from-[#833AB4] via-[#E1306C] to-[#F77737] text-white text-xs font-medium hover:opacity-90 transition-opacity">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              Instagram
            </button>
            <a href={lineShareUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#06C755] text-white text-xs font-medium hover:bg-[#05b04c] transition-colors">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/></svg>
              LINE
            </a>
          </div>
        </div>

        <div className="flex justify-center gap-3 flex-wrap">
          <Button variant="outline" onClick={restart} className="rounded-lg border-slate-300 text-slate-600 hover:bg-slate-50">
            もう一度挑戦
          </Button>
          <Link href="/register">
            <Button className="bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg">
              本試験に申し込む
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <span className="text-xs font-semibold text-indigo-600 tracking-wider">
          問題 {current + 1} / {questions.length}
        </span>
        <div className="flex gap-1.5">
          {questions.map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full transition-colors ${
                i < current ? "bg-indigo-600" : i === current ? "bg-indigo-400" : "bg-slate-300"
              }`}
            />
          ))}
        </div>
      </div>

      <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-6 mb-6">
        <p className="text-[15px] font-medium text-slate-800 leading-relaxed">{q.question}</p>
      </div>

      <div className="space-y-3 mb-6">
        {q.choices.map((choice, i) => {
          let cls = "w-full text-left rounded-xl border p-4 text-sm leading-relaxed transition-all ";
          if (selected === null) {
            cls += "border-slate-200 bg-white text-slate-700 hover:border-indigo-300 hover:bg-indigo-50/50 cursor-pointer";
          } else if (i === q.correct) {
            cls += "border-emerald-400 bg-emerald-50 text-emerald-800 font-medium";
          } else if (i === selected) {
            cls += "border-red-400 bg-red-50 text-red-800";
          } else {
            cls += "border-slate-200 bg-white/60 text-slate-400 opacity-60";
          }
          return (
            <button key={i} className={cls} onClick={() => handleSelect(i)} disabled={selected !== null}>
              <span className="flex items-start gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full border border-current/30 flex items-center justify-center text-xs font-medium mt-0.5">
                  {String.fromCharCode(65 + i)}
                </span>
                <span>{choice}</span>
              </span>
              {selected !== null && i === q.correct && (
                <span className="flex items-center gap-1.5 mt-2 ml-9 text-xs text-emerald-600">
                  <CheckCircle2 className="w-3.5 h-3.5" /> 正解
                </span>
              )}
              {selected === i && i !== q.correct && (
                <span className="flex items-center gap-1.5 mt-2 ml-9 text-xs text-red-500">
                  <XCircle className="w-3.5 h-3.5" /> 不正解
                </span>
              )}
            </button>
          );
        })}
      </div>

      {selected !== null && (
        <div className={`rounded-xl p-5 mb-6 text-sm leading-relaxed ${
          selected === q.correct
            ? "bg-emerald-50 border border-emerald-200 text-emerald-700"
            : "bg-amber-50 border border-amber-200 text-amber-800"
        }`}>
          {q.explanation}
        </div>
      )}

      {selected !== null && (
        <div className="text-center">
          <Button onClick={handleNext} className="bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg px-8">
            {current < questions.length - 1 ? "次の問題へ" : "結果を見る"}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}
    </div>
  );
}

export default function Home() {
  const { data: announcements = [] } = trpc.announcements.list.useQuery();
  const { user } = useAuth();

  usePageMeta({
    title: "Biz-AI検定 | AI活用スキルを証明するビジネス検定",
    description:
      "AI活用の実務スキルを問うオンライン検定「Biz-AI検定」。無料のサンプル問題で腕試し！AssociateからSpecialistまで、あなたのAIリテラシーを証明するデジタルバッジを取得しましょう。全国どこからでもオンライン受験可能です。",
  });

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 h-16 flex justify-between items-center">
          <Link href="/">
            <span className="text-xl font-bold text-indigo-700 tracking-wide">Biz-AI</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-[13px] text-slate-500 font-medium">
            <a href="#about" className="hover:text-indigo-600 transition-colors">資格について</a>
            <a href="#levels" className="hover:text-indigo-600 transition-colors">試験レベル</a>
            <a href="#sample" className="hover:text-indigo-600 transition-colors">お試し受験</a>
            <a href="#details" className="hover:text-indigo-600 transition-colors">試験概要</a>
            <a href="#faq" className="hover:text-indigo-600 transition-colors">FAQ</a>
          </nav>
          <div className="flex items-center gap-3">
            {user ? (
              <Link href="/my-page">
                <Button size="sm" className="bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg px-5 text-[13px] font-medium">マイページ</Button>
              </Link>
            ) : (
              <>
                <Link href="/login">
                  <Button variant="ghost" size="sm" className="text-slate-500 hover:text-indigo-600 text-[13px]">ログイン</Button>
                </Link>
                <Link href="/signup">
                  <Button size="sm" className="bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg px-5 text-[13px] font-medium">無料登録</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-purple-800 to-indigo-700"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-purple-400 blur-[120px]"></div>
          <div className="absolute bottom-[-20%] left-[-10%] w-[400px] h-[400px] rounded-full bg-indigo-400 blur-[100px]"></div>
          <div className="absolute top-[30%] left-[40%] w-[200px] h-[200px] rounded-full bg-blue-300 blur-[80px]"></div>
        </div>

        <div className="relative z-10 px-6 pt-20 pb-24 md:pt-28 md:pb-32">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/20 bg-white/10 text-[12px] text-white/80 mb-8 backdrop-blur-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
              ビジネス特化型 生成AI資格試験
            </div>

            <h1 className="text-5xl md:text-7xl font-black text-white mb-4 tracking-tight leading-[1.1]">
              Biz-AI
            </h1>
            <p className="text-lg md:text-xl text-indigo-200 font-medium mb-3">
              仕事で生成AIを成果につなげる力を、客観的に証明する。
            </p>
            <p className="text-sm text-indigo-300/80 mb-10 max-w-xl mx-auto leading-relaxed">
              暗記ではなく、業務での「状況判断力」を測る実践型の資格試験。<br className="hidden md:block" />
              就職・キャリアアップ・DX推進の強みになります。
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-12">
              <Link href="/register">
                <Button size="lg" className="bg-white text-indigo-700 hover:bg-indigo-50 rounded-lg px-8 h-12 text-sm font-bold shadow-xl shadow-indigo-900/30">
                  受験申し込みはこちら
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="#sample">
                <Button size="lg" variant="outline" className="rounded-lg px-8 h-12 text-sm border-white/30 text-white hover:bg-white/10 bg-transparent">
                  お試し受験してみる
                </Button>
              </a>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto">
              {[
                { icon: Monitor, label: "完全オンライン受験" },
                { icon: Clock, label: "40分・選択式" },
                { icon: Award, label: "デジタルバッジ発行" },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10">
                  <item.icon className="w-4 h-4 text-indigo-300" />
                  <span className="text-sm text-white/90 font-medium">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Announcements */}
      {announcements.length > 0 && (
        <section className="py-8 px-6 bg-indigo-50 border-b border-indigo-100">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <Megaphone className="w-4 h-4 text-indigo-600" />
              <span className="text-xs font-bold text-indigo-600 tracking-widest uppercase">お知らせ</span>
            </div>
            <div className="space-y-3">
              {announcements.map((a) => (
                <div key={a.id} className="rounded-xl bg-white border border-indigo-100 px-6 py-4 shadow-sm">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="text-sm font-bold text-slate-800">{a.title}</h3>
                    {a.publishedAt && (
                      <span className="text-[11px] text-slate-400 shrink-0">
                        {new Date(a.publishedAt).toLocaleDateString("ja-JP", { year: "numeric", month: "long", day: "numeric" })}
                      </span>
                    )}
                  </div>
                  <p className="text-[13px] text-slate-600 mt-1.5 leading-relaxed whitespace-pre-wrap">{a.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* About */}
      <section id="about" className="py-24 md:py-32 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-bold text-indigo-600 tracking-[0.2em] uppercase mb-4">About Biz-AI</p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 leading-tight">
              「ただ使える」では差別化できない時代へ
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed max-w-2xl mx-auto">
              生成AIは、議事録やメール文の作成、提案アイデアの整理など、毎日の業務で欠かせない存在になりつつあります。
              Biz-AIは、生成AIを仕事で成果につなげる力を客観的に証明し、就職、キャリアアップや転職市場での強みとなる資格です。
            </p>
          </div>

          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 md:p-12 text-white">
            <h3 className="text-lg font-bold mb-8 text-center text-indigo-100">資格取得で得られるもの</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { title: "生成AIを活用したビジネス人材を認定", desc: "業務で成果を出せるAI活用スキルを客観的に証明します。" },
                { title: "企業のDX推進に役立つスキル証明", desc: "採用や人事評価の指標となり、組織のデジタル対応力を示せます。" },
                { title: "履歴書やキャリアアップに直結", desc: "転職や昇進において、実践的なITスキルとしてアピールできます。" },
              ].map((item, i) => (
                <div key={i} className="flex gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-300 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-sm mb-1">{item.title}</h4>
                    <p className="text-xs text-indigo-200 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Levels */}
      <section id="levels" className="py-24 md:py-32 px-6 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-bold text-indigo-600 tracking-[0.2em] uppercase mb-4">Exam Levels</p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 leading-tight">
              2つのレベルで、段階的にスキルを証明
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Associate */}
            <div className="rounded-2xl bg-white border-2 border-indigo-200 p-8 md:p-10 hover:border-indigo-400 hover:shadow-lg transition-all relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 to-purple-500"></div>
              <span className="inline-block text-xs font-bold text-indigo-500 tracking-wider uppercase mb-6">Level 01 — 初級</span>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Associate</h3>
              <p className="text-indigo-600 font-semibold text-sm mb-1">¥8,000（税込）</p>
              <p className="text-xs text-slate-400 mb-6">基礎知識と簡単な活用方法</p>
              <p className="text-sm text-slate-600 leading-relaxed mb-8">
                AIとビジネスの基本概念を理解していることを証明する入門資格です。受験資格の制限はなく、どなたでもチャレンジできます。
              </p>
              <div className="space-y-2.5 mb-8">
                {["受験資格の制限なし", "基礎知識の理解を評価", "Specialist受験の前提条件"].map((text, i) => (
                  <div key={i} className="flex items-center gap-2.5 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-indigo-500 shrink-0" />
                    {text}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-4">
                <Link href="/register">
                  <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-5 text-[13px] font-medium">
                    受験申し込み <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                  </Button>
                </Link>
                <Link href="/syllabus/1">
                  <span className="text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors">シラバス →</span>
                </Link>
              </div>
            </div>

            {/* Specialist */}
            <div className="rounded-2xl bg-gradient-to-br from-indigo-900 to-purple-900 border-2 border-indigo-700 p-8 md:p-10 hover:shadow-xl hover:shadow-indigo-900/20 transition-all relative overflow-hidden text-white">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-400 to-pink-400"></div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/20 blur-[60px] rounded-full"></div>
              <span className="inline-block text-xs font-bold text-purple-300 tracking-wider uppercase mb-6 relative">Level 02 — 中級</span>
              <h3 className="text-2xl font-bold mb-2 relative">Specialist</h3>
              <p className="text-purple-300 font-semibold text-sm mb-1 relative">¥12,000（税込）</p>
              <p className="text-xs text-indigo-300 mb-6 relative">業務改善に役立つ実践スキル</p>
              <p className="text-sm text-indigo-200 leading-relaxed mb-8 relative">
                高度な専門知識と実践スキルを証明する上級資格です。Associate合格者が対象で、AI活用を推進するリーダーに推奨します。
              </p>
              <div className="space-y-2.5 mb-8 relative">
                {["Associate合格が前提条件", "高度な専門知識を評価", "より高い合格基準"].map((text, i) => (
                  <div key={i} className="flex items-center gap-2.5 text-sm text-indigo-200">
                    <CheckCircle2 className="w-4 h-4 text-purple-300 shrink-0" />
                    {text}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-4 relative">
                <Link href="/register">
                  <Button size="sm" className="bg-white text-indigo-900 hover:bg-indigo-50 rounded-lg px-5 text-[13px] font-bold">
                    受験申し込み <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                  </Button>
                </Link>
                <Link href="/syllabus/2">
                  <span className="text-sm font-medium text-purple-300 hover:text-white transition-colors">シラバス →</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sample Quiz */}
      <section id="sample" className="py-24 md:py-32 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-bold text-indigo-600 tracking-[0.2em] uppercase mb-4">Try it now</p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 leading-tight mb-4">
              Associate レベルを体験してみよう
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed">
              実際の試験と同じ形式のサンプル問題です。業務での判断力を試してみてください。
            </p>
          </div>
          <div className="rounded-2xl border-2 border-indigo-100 bg-white p-8 md:p-10 shadow-sm">
            <SampleQuiz />
          </div>
        </div>
      </section>

      {/* Exam Details */}
      <section id="details" className="py-24 md:py-32 px-6 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-bold text-indigo-600 tracking-[0.2em] uppercase mb-4">Exam Details</p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">試験概要</h2>
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-sm">
            <table className="w-full">
              <tbody>
                {[
                  { label: "受験方式", value: "オンライン受験（自宅・職場から受験可能）" },
                  { label: "形式", value: "選択式（4択）" },
                  { label: "受験時間", value: "40分" },
                  { label: "区分", value: (
                    <div className="space-y-1">
                      <div>Associate　初級：基礎知識と簡単な活用方法　¥8,000</div>
                      <div>Specialist　中級：業務改善に役立つ実践スキル　¥12,000</div>
                    </div>
                  )},
                  { label: "開催頻度", value: "各コースごと2ヶ月に1回実施" },
                  { label: "合格基準", value: "80%（50問中40問以上正解）" },
                  { label: "合格証", value: "デジタルバッジ発行（有効期限：合格から2年間）" },
                  { label: "申込方法", value: "Biz-AI公式サイトよりお申し込みください。" },
                ].map((row, i) => (
                  <tr key={i} className="border-b border-slate-100 last:border-0">
                    <th className="py-5 px-6 text-left bg-indigo-50/50 text-sm font-bold text-indigo-900 w-[160px] align-top whitespace-nowrap">
                      {row.label}
                    </th>
                    <td className="py-5 px-6 text-sm text-slate-600 leading-relaxed">{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Flow */}
      <section className="py-24 md:py-32 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-bold text-indigo-600 tracking-[0.2em] uppercase mb-4">How it works</p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">受験の流れ</h2>
          </div>
          <div className="space-y-0">
            {[
              { num: "01", title: "アカウント登録", desc: "メールアドレスとパスワードで登録。1分で完了します。" },
              { num: "02", title: "試験セッションに申込", desc: "受験したいレベルとセッションを選んで申し込みます。" },
              { num: "03", title: "オンラインで受験", desc: "指定の日時にブラウザからアクセスして受験します。" },
              { num: "04", title: "スコアと合否を確認", desc: "受験後すぐに結果を確認。設問ごとの解説も見られます。" },
              { num: "05", title: "認定バッジを取得", desc: "合格するとデジタルバッジが発行。認定証もダウンロードできます。" },
            ].map((item) => (
              <div key={item.num} className="flex gap-8 py-8 border-b border-slate-200 last:border-0 group">
                <span className="text-3xl font-light text-indigo-200 tabular-nums shrink-0 pt-0.5 group-hover:text-indigo-400 transition-colors">{item.num}</span>
                <div>
                  <h3 className="font-bold text-slate-800 text-lg mb-1">{item.title}</h3>
                  <p className="text-sm text-slate-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 md:py-32 px-6 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-bold text-indigo-600 tracking-[0.2em] uppercase mb-4">FAQ</p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">よくある質問</h2>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {[
              { q: "受験資格はありますか？", a: "Associate（入門）はどなたでも受験できます。Specialist（上級）はAssociateに合格していることが前提条件です。" },
              { q: "どこで受験できますか？", a: "完全オンラインです。インターネット環境とPC・タブレットがあれば、自宅やオフィスから受験できます。専用ソフトのインストールは不要です。" },
              { q: "合格証明書は発行されますか？", a: "はい。合格者にはデジタル認定バッジが付与されます。マイページから確認でき、認定証のダウンロードも可能です。" },
              { q: "不合格でも再受験できますか？", a: "はい。次回の試験セッションで再度受験可能です。結果の詳細フィードバックを参考に準備してください。" },
              { q: "バッジに有効期限はありますか？", a: "認定バッジの有効期限は取得日から2年間です。期限が近づくと事前にメールでお知らせし、再受験による更新も可能です。" },
              { q: "学習教材はありますか？", a: "本試験は業務での実践的な判断力を問うものであり、特定の事前学習教材は必須としておりません。公式サイトのシラバスやサンプル問題を参考に、ご自身の業務経験と照らし合わせてご準備ください。" },
            ].map((item, i) => (
              <AccordionItem key={i} value={`q${i}`} className="border-b border-slate-200 py-1">
                <AccordionTrigger className="text-left text-[15px] font-medium text-slate-700 hover:text-indigo-600 hover:no-underline py-5 [&[data-state=open]]:text-indigo-600">{item.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-slate-500 leading-relaxed pb-5">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 px-6 bg-gradient-to-br from-indigo-900 via-purple-800 to-indigo-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[300px] bg-purple-400 blur-[100px] rounded-full"></div>
        </div>
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-5">今すぐ受験申し込みを。</h2>
          <p className="text-indigo-200 mb-10 text-[15px]">アカウント登録後、希望のセッションに申し込めます。</p>
          <Link href="/register">
            <Button size="lg" className="bg-white text-indigo-700 hover:bg-indigo-50 rounded-lg px-10 h-12 text-sm font-bold shadow-xl">
              受験申し込みはこちら
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-12 px-6 bg-slate-900 text-slate-400">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between gap-8 mb-10">
            <div>
              <span className="text-lg font-bold text-white">Biz-AI</span>
              <p className="text-[13px] text-slate-400 mt-2 max-w-xs leading-relaxed">
                ビジネス特化型 生成AI資格試験
              </p>
            </div>
            <div className="flex gap-16">
              <div>
                <p className="text-xs font-bold text-white tracking-[0.2em] uppercase mb-4">Platform</p>
                <ul className="space-y-3 text-[13px]">
                  <li><Link href="/syllabus" className="hover:text-white transition-colors">シラバス</Link></li>
                  {user ? (
                    <li><Link href="/my-page" className="hover:text-white transition-colors">マイページ</Link></li>
                  ) : (
                    <>
                      <li><Link href="/login" className="hover:text-white transition-colors">ログイン</Link></li>
                      <li><Link href="/signup" className="hover:text-white transition-colors">新規登録</Link></li>
                    </>
                  )}
                </ul>
              </div>
              <div>
                <p className="text-xs font-bold text-white tracking-[0.2em] uppercase mb-4">Exams</p>
                <ul className="space-y-3 text-[13px]">
                  <li><Link href="/syllabus/1" className="hover:text-white transition-colors">Associate</Link></li>
                  <li><Link href="/syllabus/2" className="hover:text-white transition-colors">Specialist</Link></li>
                </ul>
              </div>
              <div>
                <p className="text-xs font-bold text-white tracking-[0.2em] uppercase mb-4">Legal</p>
                <ul className="space-y-3 text-[13px]">
                  <li><Link href="/terms" className="hover:text-white transition-colors">利用規約</Link></li>
                  <li><Link href="/privacy" className="hover:text-white transition-colors">プライバシーポリシー</Link></li>
                  <li><Link href="/tokushoho" className="hover:text-white transition-colors">特定商取引法に基づく表記</Link></li>
                </ul>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8">
            <p className="text-[12px] text-slate-500">&copy; 2026 合同会社AIM All Rights Reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
